import type { Enums } from './db/database.types';

export type MemberRole = Enums<'member_role'>;
export type ContactType = Enums<'contact_type'>;
export type CashAccountType = Enums<'cash_account_type'>;
export type InvoiceType = Enums<'invoice_type'>;
export type InvoiceStatus = Enums<'invoice_status'>;
export type TxType = Enums<'transaction_type'>;
export type CategoryKind = Enums<'category_kind'>;

// ── Phase 2 (stock & items) ─────────────────────────────────────────────────
export type ItemType = Enums<'item_type'>;
export type ItemStatus = Enums<'item_status'>;
export type BomStatus = Enums<'bom_status'>;
export type BomOpType = Enums<'bom_op_type'>;
export type RoutingType = Enums<'routing_type'>;
export type WorkCenterType = Enums<'work_center_type'>;
export type WarehouseType = Enums<'warehouse_type'>;
export type LocationType = Enums<'location_type'>;
export type LabelEntity = Enums<'label_entity'>;
export type LabelTemplate = Enums<'label_template'>;
export type StockMovementReason = Enums<'stock_movement_reason'>;
export type StockMovementKind = Enums<'stock_movement_kind'>;
export type StockMovementSource = Enums<'stock_movement_source'>;
export type ImportKind = Enums<'import_kind'>;
export type ImportStatus = Enums<'import_status'>;
export type ImportRowStatus = Enums<'import_row_status'>;

/** W-04 type-chip order: the `type_sort` of `v_items_list`, so both sides sort alike. */
export const ITEM_TYPE_ORDER: ItemType[] = ['raw_material', 'semi_finished', 'finished_good', 'packaging', 'consumable', 'service'];

/** The eight work-centre kinds the settings drawer offers, in the design's order (W-31 §5.3). */
export const WORK_CENTER_TYPE_ORDER: WorkCenterType[] = [
  'cutting',
  'folding',
  'foil_print',
  'digital_print',
  'packing',
  'palletising',
  'qc',
  'other',
];

/** Location kinds a user may pick (W-31 §4.4); `transit` is set by the engine, never by hand. */
export const LOCATION_TYPE_ORDER: LocationType[] = ['storage', 'receiving', 'shipping', 'production', 'quarantine', 'scrap'];

/** Warehouse kinds the drawer offers — `fba` is opened by the marketplace picker, not typed in. */
export const WAREHOUSE_TYPE_ORDER: WarehouseType[] = ['own', 'supplier', 'transit', 'third_party'];

/**
 * Role groups — keep in sync with RLS policies (packages/db/policies.md).
 * The Phase 2 groups (C25) mirror the SQL helpers of migration 20260923000001 one for one:
 * `stockRead` = private.stock_reader · `stockWrite` = private.stock_writer (= opsWrite) ·
 * `stockApprove` = `masterWrite` = private.master_writer · `seesCosts` = private.sees_costs.
 */
export const ROLE_GROUPS = {
  write: ['owner', 'partner'] as MemberRole[],
  accountingRead: ['owner', 'partner', 'accountant_readonly'] as MemberRole[],
  opsWrite: ['owner', 'partner', 'operator'] as MemberRole[],
  seesCosts: ['owner', 'partner', 'accountant_readonly'] as MemberRole[],
  stockRead: ['owner', 'partner', 'operator', 'accountant_readonly', 'guest'] as MemberRole[],
  stockWrite: ['owner', 'partner', 'operator'] as MemberRole[],
  stockApprove: ['owner', 'partner'] as MemberRole[],
  masterWrite: ['owner', 'partner'] as MemberRole[],
};

export function can(role: MemberRole | null | undefined, group: keyof typeof ROLE_GROUPS): boolean {
  return !!role && ROLE_GROUPS[group].includes(role);
}

/** Supported transaction currencies (design: TRY · USD · EUR · GBP). */
export const TRANSACTION_CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'] as const;
export const BASE_CURRENCY = 'TRY';

/** UI transaction kinds (design W-28) → DB transaction_type. */
export const TX_KINDS = ['receipt', 'payment', 'transfer', 'expense', 'income', 'fx_diff'] as const;
export type TxKind = (typeof TX_KINDS)[number];

export const MARKETPLACE_ORDER = ['US', 'UK', 'DE', 'FR', 'IT', 'ES', 'NL', 'SE', 'PL', 'BE'] as const;
